package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelCategory
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_panel_category_evolution
import portalvv.shared.generated.resources.compras_panel_col_category
import portalvv.shared.generated.resources.compras_panel_col_month_value
import portalvv.shared.generated.resources.compras_panel_col_variation_percent
import portalvv.shared.generated.resources.compras_panel_no_data

private const val CATEGORY_NAME_WEIGHT = 1.2f
private const val CATEGORY_MONTH_WEIGHT = 2f

private data class CategoryEvolutionValue(
    val totalCents: Long,
    val variationPercent: Double?,
)

@Composable
internal fun CategoryEvolutionTable(
    categories: List<PurchasesPanelCategory>,
    months: List<MonthPeriod>,
) {
    val entries =
        categories
            .filter { it.totalCents > 0L }
            .sortedByDescending { it.totalCents }
    val activeMonths = activePurchaseMonths(categories, months)
    val valuesByCategory =
        entries.associateWith { category ->
            categoryEvolutionValues(category, activeMonths)
        }
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        Text(
            text = stringResource(Res.string.compras_panel_category_evolution),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        if (entries.isEmpty() || activeMonths.isEmpty()) {
            Text(
                text = stringResource(Res.string.compras_panel_no_data),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            return@Column
        }
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val chunkSize = monthsPerTable(maxWidth)
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                activeMonths.chunked(chunkSize).forEach { chunk ->
                    CategoryEvolutionTableChunk(
                        entries = entries,
                        months = chunk,
                        valuesByCategory = valuesByCategory,
                    )
                }
            }
        }
    }
}

@Composable
private fun CategoryEvolutionTableChunk(
    entries: List<PurchasesPanelCategory>,
    months: List<MonthPeriod>,
    valuesByCategory: Map<PurchasesPanelCategory, Map<MonthPeriod, CategoryEvolutionValue>>,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            CategoryEvolutionHeader(months = months)
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            entries.forEachIndexed { index, category ->
                CategoryEvolutionRow(
                    category = category,
                    months = months,
                    values = valuesByCategory[category].orEmpty(),
                )
                if (index < entries.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
            }
        }
    }
}

@Composable
private fun CategoryEvolutionHeader(months: List<MonthPeriod>) {
    Column(modifier = Modifier.fillMaxWidth().background(AppPalette.Neutral50)) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = stringResource(Res.string.compras_panel_col_category),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.weight(CATEGORY_NAME_WEIGHT),
            )
            months.forEach { month ->
                Text(
                    text = MonthCalendar.shortLabel(month),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.weight(CATEGORY_MONTH_WEIGHT),
                )
            }
        }
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space4),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Spacer(modifier = Modifier.weight(CATEGORY_NAME_WEIGHT))
            months.forEach {
                Row(modifier = Modifier.weight(CATEGORY_MONTH_WEIGHT)) {
                    CategoryEvolutionHeaderCell(
                        text = stringResource(Res.string.compras_panel_col_month_value),
                    )
                    CategoryEvolutionHeaderCell(
                        text = stringResource(Res.string.compras_panel_col_variation_percent),
                    )
                }
            }
        }
    }
}

@Composable
private fun RowScope.CategoryEvolutionHeaderCell(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = TextAlign.End,
        modifier = Modifier.weight(1f).padding(horizontal = AppSpacing.space4),
    )
}

@Composable
private fun CategoryEvolutionRow(
    category: PurchasesPanelCategory,
    months: List<MonthPeriod>,
    values: Map<MonthPeriod, CategoryEvolutionValue>,
) {
    val categoryUi = PurchasesCategory.entries.firstOrNull { it.type == category.type }
    val name = categoryUi?.let { stringResource(it.label) } ?: category.type.name
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier.weight(CATEGORY_NAME_WEIGHT),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            categoryUi?.let {
                Icon(
                    imageVector = it.icon,
                    contentDescription = null,
                    tint = it.iconColor,
                    modifier = Modifier.size(18.dp),
                )
            }
            Text(
                text = name,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
        }
        months.forEach { month ->
            val value = values[month]
            Row(modifier = Modifier.weight(CATEGORY_MONTH_WEIGHT)) {
                CategoryEvolutionValueCell(
                    text = value?.let { if (it.totalCents > 0L) money(it.totalCents) else NO_TABLE_VALUE } ?: NO_TABLE_VALUE,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                CategoryEvolutionValueCell(
                    text = value?.variationPercent?.let { variationText(it) } ?: NO_TABLE_VALUE,
                    color =
                        when {
                            value?.variationPercent == null -> MaterialTheme.colorScheme.onSurfaceVariant
                            value.variationPercent < 0.0 -> AppPalette.Success900
                            value.variationPercent > 0.0 -> AppPalette.Error900
                            else -> MaterialTheme.colorScheme.onSurface
                        },
                )
            }
        }
    }
}

@Composable
private fun RowScope.CategoryEvolutionValueCell(
    text: String,
    color: Color,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = color,
        textAlign = TextAlign.End,
        modifier = Modifier.weight(1f).padding(horizontal = AppSpacing.space4),
    )
}

private fun categoryEvolutionValues(
    category: PurchasesPanelCategory,
    months: List<MonthPeriod>,
): Map<MonthPeriod, CategoryEvolutionValue> {
    var previousCents: Long? = null
    return months.associateWith { period ->
        val currentCents =
            category.monthly
                .firstOrNull { it.year == period.year && it.month == period.month }
                ?.totalCents ?: 0L
        val previous = previousCents
        val variation =
            if (currentCents > 0L && previous != null && previous > 0L) {
                (currentCents - previous) * 100.0 / previous
            } else {
                null
            }
        if (currentCents > 0L) {
            previousCents = currentCents
        }
        CategoryEvolutionValue(currentCents, variation)
    }
}
